import { langAbbrev } from "../../data/langs.ts";

/**
 * AsLangSelector Web Component
 * 
 * A simple language selector that:
 * 1. Displays available languages
 * 2. Dispatches events when language is changed
 * 3. Uses custom element lifecycle hooks
 */
export class AsLangSelector extends HTMLElement {
  private selectElement: HTMLSelectElement | null = null;
  private currentLanguage: string = 'en';

  constructor() {
    super();
    this.createUI();
    this.addEventListeners();
  }

  connectedCallback() {
    // Listen for language changes on window (bubbled from as-app)
    window.addEventListener('as-language-changed', this.handleLanguageChanged);
    
    // Request initial language from as-app (delay to ensure as-app is ready)
    setTimeout(() => {
      this.dispatchEvent(new CustomEvent('as-get-init-lang', {
        bubbles: true,
        composed: true,
        detail: { requester: this }
      }));
    }, 100);
  }

  disconnectedCallback() {
    // Clean up event listeners
    window.removeEventListener('as-language-changed', this.handleLanguageChanged);
  }

  private handleLanguageChanged = (event: CustomEvent) => {
    if (event.detail && event.detail.lang) {
      this.setLanguage(event.detail.lang);
    }
  }

  /**
   * Create the UI elements directly without using templates
   */
  private createUI(): void {
    // Create container
    const container = document.createElement('div');
    container.className = 'as-lang-selector-container';
    
    // Create select element
    const select = document.createElement('select');
    select.id = 'lang-selector';
    
    // Get available languages
    const languages = ['en', 'de', 'fr', 'jp', 'cn'];
    
    // Create options
    languages.forEach(lang => {
      const option = document.createElement('option');
      option.value = lang;
      option.textContent = langAbbrev[lang] || lang;
      option.selected = lang === this.currentLanguage;
      select.appendChild(option);
    });
    
    // Apply styles
    this.style.cssText = `
      display: inline-block;
      font-family: sans-serif;
    `;
    
    select.style.cssText = `
      font-size: 1rem;
      padding: 0.3rem;
      border-radius: 4px;
      border: 1px solid #ccc;
      background: none;
      border: none;
      width: fit-content;

    `;
    
    // Add to component
    container.appendChild(select);
    this.appendChild(container);
    
    // Store reference to select element
    this.selectElement = select;
  }

  /**
   * Add event listeners for language changes
   */
  private addEventListeners(): void {
    this.selectElement?.addEventListener('change', (event: Event) => {
      const target = event.target as HTMLSelectElement;
      const newLang = target.value;
      
      // Only proceed if language actually changed
      if (this.currentLanguage !== newLang) {
        // Update internal state
        this.currentLanguage = newLang;
        
        // Dispatch event to notify parent components (will bubble up to as-app)
        this.dispatchEvent(new CustomEvent('as-change-language', {
          bubbles: true,
          composed: true,
          detail: {
            lang: newLang
          }
        }));
      }
    });
  }


  /**
   * Update the selected option in the dropdown
   */
  private updateSelectedLanguage(): void {
    if (this.selectElement) {
      Array.from(this.selectElement.options).forEach(option => {
        option.selected = option.value === this.currentLanguage;
      });
    }
  }

  /**
   * Set language programmatically - does NOT dispatch events (used when responding to as-app changes)
   */
  public setLanguage(lang: string): void {
    if (this.currentLanguage !== lang) {
      this.currentLanguage = lang;
      this.updateSelectedLanguage();
      // No event dispatching - this is for syncing with as-app, not user interaction
    }
  }
}

// Check if the element is already defined before registering it
if (!customElements.get('as-lang-selector')) {
  customElements.define('as-lang-selector', AsLangSelector);
} else {
  console.warn("as-lang-selector already defined");
}
